/**
 * One handler for `HtmlViewer`'s `onAppRunNotice`, shared by every surface that
 * runs a PDApp (#184 Phase 4).
 *
 * Three components mount `HtmlViewer` with a live app in it — the app deck, the
 * editor's preview, and the share/session run view. A copy of this in each is
 * three chances for them to describe one failure three ways, which is the drift
 * `describeUa` was extracted to stop between the Sessions and Runs tabs.
 *
 * ── The words come from the Runs panel's own vocabulary ──────────────────────
 *
 * `runLog.kind.*` is what the panel prints for the same code, so the toast and
 * the row a reader opens next say the same thing. An UNKNOWN code falls through
 * to its raw value exactly as the panel does — the server's list is the one
 * authority (`PLATFORM_NOTICE_CODES`), so a code added there ships without a
 * client change and a stale client degrades to a plain code rather than silence.
 */
import { useCallback } from 'react'
import { useTranslation } from '@papan/i18n'
import { useToast } from '@papan/core-ui/ui'
import type { AppRunNotice } from './app-run-notice.js'

export function useAppRunNoticeToast(): (notice: AppRunNotice) => void {
  const { t } = useTranslation('files')
  const toast = useToast()

  return useCallback((notice: AppRunNotice) => {
    /**
     * A run can BOTH fail and have lost events. The failure leads, because it is
     * what the person watching just experienced; `'warnings'` is the code the
     * server sends when losses were the ONLY thing wrong — a run that exited 0
     * and looked completely healthy.
     */
    const lead = notice.code === 'warnings'
      ? (notice.warnings ?? []).map((w) => t(`runLog.kind.${w}`, { defaultValue: w })).join(' · ')
      : t(`runLog.kind.${notice.code}`, { defaultValue: notice.code })

    // The toast says WHAT and WHERE TO LOOK, never why — the why is a traceback
    // or a Docker message, and neither belongs in four seconds of chrome. The
    // reference id is included because its only use is being pasted into a
    // report, which is the same reason the Runs panel makes it copyable.
    // The lane is INTERPOLATED, never spelled into the sentence. Runs is a
    // filter chip inside the Logs panel, not a tab of its own (this same phase
    // folded it in), and its label is translated — so a sentence naming a
    // "Runs tab" was wrong about the control in English and printed an
    // untranslated English word in the three other locales, pointing at a chip
    // that reads 运行 / 執行 / Larian on screen. One key, one spelling.
    const lane = t('runLog.tabRuns')
    const body = notice.requestId
      ? t('runLog.notice.bodyWithRef', { lane, ref: notice.requestId })
      : t('runLog.notice.body', { lane })

    toast.error(`${lead} — ${body}`)
  }, [t, toast])
}
